import type { PlanChecks } from '@repo/front-end-utils';

/**
 * The Floor Plan step's *manufacturer identified* score on the step list (`floor-plan-page-checks`,
 * R7; frame 02, note 1): the rule for which number, if any, the step list shows.
 */
export interface PlanScore {
  /** The twin the score was worked out for — another twin's number is never shown. */
  twinId: number | undefined;
  percent: number;
}

/** A score the model could actually work out: plan known, register read, nothing failed, and
 * something to score (`percent` is `null` when nothing is). */
export const planScoreFrom = (checks: PlanChecks): number | null =>
  checks.known && checks.registerKnown && !checks.failed && typeof checks.score.percent === 'number'
    ? checks.score.percent
    : null;

/**
 * The next score to keep, from the planner's latest report. A `null` report means the model is
 * unavailable — out of scope, or not buildable — and clears the score. Leaving the step reports
 * nothing (`onPlanChecks` is silent on unmount), so the last score stays on the step list while the
 * operator is on the other steps. The previous object is returned whenever nothing shown would change,
 * so React skips the render — the model is rebuilt on every layout change, and the page is large.
 */
export const nextPlanScore = (
  prev: PlanScore | null,
  checks: PlanChecks | null,
  twinId: number | undefined,
): PlanScore | null => {
  const percent = checks ? planScoreFrom(checks) : null;
  if (percent === null) return prev === null ? prev : null;
  return prev && prev.twinId === twinId && prev.percent === percent ? prev : { twinId, percent };
};

/**
 * The kept score, brought up to date from the page's own identify model while the planner is not
 * mounted (Copilot review, round 3). Register edits on the Equipment step change that model, and the
 * planner's Checks model takes its score from the same `buildIdentifyModel` over the same joined plan,
 * so it is the same number. Only a score the Checks model has already worked out for this twin is
 * updated — nothing is shown that the Checks gate never passed — and one the identify model can no
 * longer work out (not built, or nothing to score) is cleared.
 */
export const syncPlanScore = (
  prev: PlanScore | null,
  identify: { score: { percent: number | null } } | null,
  twinId: number | undefined,
): PlanScore | null => {
  if (!prev || prev.twinId !== twinId) return prev;
  const percent = identify ? identify.score.percent : null;
  if (typeof percent !== 'number') return null;
  return prev.percent === percent ? prev : { twinId, percent };
};

/** What the Floor Plan item on the step list shows for `twinId`, or nothing. */
export const planScoreShown = (
  score: PlanScore | null,
  twinId: number | undefined,
): string | undefined => (score && score.twinId === twinId ? `${score.percent} %` : undefined);
